import * as Icons from '@ant-design/icons'
import { Button, ButtonProps, Card, CardProps, Flex, Typography } from 'antd'
import { ParagraphProps } from 'antd/es/typography/Paragraph'
import { TitleProps } from 'antd/es/typography/Title'
import React, { useEffect } from 'react'
import { DesignSystem } from '~/core/designSystem'
import { Api } from '~/core/trpc'
import { useAuthorization } from '../../hooks/useAuthorization'
import { useProvider } from '../../hooks/useProvider'
import { ModalAuthentication } from '../ModalAuthentication'

const { Paragraph, Text } = Typography

export type CardAccountAuthorizationUIProps = {
  title: string
  description: string
  iconAntd: keyof typeof Icons
  propsCard?: CardProps
  propsTitle?: TitleProps
  propsDescription?: ParagraphProps
  propsButton?: ButtonProps
}

type Props = {
  providerAccountId: string
  scope: string
  ui: CardAccountAuthorizationUIProps
  authorized?: boolean
  onSuccess?: () => void
  onCorrupted?: () => void
}

export const CardAccountAuthorization: React.FC<Props> = ({
  providerAccountId,
  scope,
  ui,
  authorized,
  onSuccess,
  onCorrupted,
}) => {
  const isMobile = DesignSystem.use().isMobile

  const accountQuery = Api.providerAccount.findUnique.useQuery(
    {
      where: { id: providerAccountId },
      include: {
        scopes: {
          where: { scope },
        },
      },
    },
    { enabled: !!providerAccountId },
  )

  const providerAccount = accountQuery.data
  const providerAccountScope = providerAccount?.scopes?.[0]

  const isProviderAccountCorrupted = providerAccount?.status === 'CORRUPTED'
  const isScopeCorrupted = providerAccountScope?.status === 'CORRUPTED'

  const { providerClient: provider } = useProvider({
    providerKey: providerAccount?.providerKey,
  })

  const getExtraAuthParams = () => {
    if (!providerAccount) {
      return {}
    }

    const configuration: any = providerAccount.configuration

    switch (providerAccount.providerKey) {
      case 'googleDrive':
      case 'googleCalendar':
      case 'gmail':
        return {
          login_hint: configuration?.email,
          prompt: undefined,
        }
      default:
        return {}
    }
  }

  useEffect(() => {
    if (isScopeCorrupted) {
      onCorrupted?.()
    }
  }, [isScopeCorrupted])

  const authorization = useAuthorization({
    provider,
    providerAccount,
    scope,
    extraAuthParams: getExtraAuthParams(),
    onSubmit() {
      onSuccess?.()
      accountQuery.refetch()
    },
  })

  // eslint-disable-next-line import/namespace
  const IconAntd: any = ui.iconAntd ? Icons[ui.iconAntd] : Icons.UserOutlined

  const handleAuthorize = () => {
    authorization.start()
  }

  const Action = () => {
    const propsButton = ui.propsButton

    if (!isScopeCorrupted && authorized) {
      const Icon = <Icons.CheckCircleOutlined />
      return (
        <Button
          block={isMobile}
          icon={Icon}
          variant="filled"
          color="green"
          {...propsButton}
          onClick={() => {}}
          // disabled
        >
          Authorized
        </Button>
      )
    }

    if (isProviderAccountCorrupted) {
      return (
        <Button variant="outlined" block={isMobile} {...propsButton} disabled>
          Reauthorize
        </Button>
      )
    }

    if (authorized && isScopeCorrupted) {
      return (
        <Button
          onClick={handleAuthorize}
          color="danger"
          variant="outlined"
          block={isMobile}
          {...propsButton}
        >
          Reauthorize
        </Button>
      )
    }

    if (!providerAccount || !provider) {
      return <Button type="text" loading block={isMobile} {...propsButton} />
    }

    return (
      <Button onClick={handleAuthorize} block={isMobile} {...propsButton}>
        Authorize
      </Button>
    )
  }

  return (
    <>
      <Card size="small" {...ui.propsCard}>
        <Flex
          align={isMobile ? 'flex-start' : 'center'}
          justify="space-between"
          className="h-full"
          vertical={isMobile}
        >
          <Flex justify="space-between" vertical className="h-full" gap={8}>
            <Flex gap={8} align="center">
              {IconAntd && <IconAntd />}
              <Text strong {...ui.propsTitle}>
                {ui.title}
              </Text>
            </Flex>
            <Paragraph type="secondary" {...ui.propsDescription}>
              {ui.description}
            </Paragraph>
            {isProviderAccountCorrupted && (
              <Paragraph type="warning">
                Re-connect your account to continue using this feature
              </Paragraph>
            )}
          </Flex>
          <Action />
        </Flex>
      </Card>

      <ModalAuthentication {...authorization.propsModal} />
    </>
  )
}
